// src/pages/SubscriptionPage.js
// Vendor-facing billing & subscription page.
// Shows current pricing plan, monthly usage stats, and transaction ledger.

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api         from '../services/api';

const MODEL_ICONS = {
  COMMISSION_TIERED:   '📊',
  PER_ORDER_TIERED:    '🎯',
  FLAT_PLUS_PER_ORDER: '📅',
};

const MODEL_LABELS = {
  COMMISSION_TIERED:   'Tiered Commission',
  PER_ORDER_TIERED:    'Per-Order Tiered',
  FLAT_PLUS_PER_ORDER: 'Monthly Flat + Per Order',
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmt(n) { return Number(n || 0).toFixed(2); }

// ── Plan card ─────────────────────────────────────────────────
function PlanCard({ plan }) {
  if (!plan) return (
    <div className="card" style={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: 'var(--text3)', fontSize: 13 }}>No plan assigned — using platform default.</div>
    </div>
  );

  const cfg = { ...(plan.config || {}), ...(plan.customConfig || {}) };

  return (
    <div className="card">
      <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
        Current Plan
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <span style={{ fontSize: 32 }}>{MODEL_ICONS[plan.modelType] || '📋'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{plan.modelName || MODEL_LABELS[plan.modelType] || plan.modelType}</div>
          <div style={{ fontSize: 12, color: 'var(--text2)' }}>{MODEL_LABELS[plan.modelType]}</div>
        </div>
        {plan.isDefault && (
          <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 20, background: 'var(--bg3)', color: 'var(--text3)', border: '1px solid var(--border)' }}>
            Default
          </span>
        )}
      </div>

      <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.9, padding: '10px 12px', background: 'var(--bg3)', borderRadius: 8 }}>
        {plan.modelType === 'COMMISSION_TIERED' && (
          <>
            {(cfg.tiers || []).map((t, i) => (
              <div key={i}>
                {t.upTo >= 999999 ? 'Remaining amount' : `Up to AED ${t.upTo}`}:&nbsp;
                <strong style={{ color: 'var(--text)' }}>{t.pct}%</strong>
              </div>
            ))}
            {cfg.minFee != null && (
              <div>Min fee: <strong style={{ color: 'var(--text)' }}>AED {cfg.minFee}</strong> / order</div>
            )}
            {cfg.maxFee != null && (
              <div>Max fee: <strong style={{ color: 'var(--text)' }}>AED {cfg.maxFee}</strong> / order</div>
            )}
          </>
        )}
        {plan.modelType === 'PER_ORDER_TIERED' && (
          <>
            {(cfg.tiers || []).map((t, i) => (
              <div key={i}>Up to AED {t.upTo}: <strong style={{ color: 'var(--text)' }}>AED {t.fee}</strong> / order</div>
            ))}
            {cfg.maxFee != null && (
              <div>Max fee: <strong style={{ color: 'var(--text)' }}>AED {cfg.maxFee}</strong> / order</div>
            )}
          </>
        )}
        {plan.modelType === 'FLAT_PLUS_PER_ORDER' && (
          <>
            <div>Monthly flat: <strong style={{ color: 'var(--text)' }}>AED {cfg.monthlyFlat ?? 2000}</strong></div>
            <div>Per-order fee: <strong style={{ color: 'var(--text)' }}>AED {cfg.perOrderFee ?? 1}</strong></div>
          </>
        )}
      </div>

      {plan.effectiveFrom && (
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text3)' }}>
          Effective {new Date(plan.effectiveFrom).toLocaleDateString()}
          {plan.effectiveTo && ` · expires ${new Date(plan.effectiveTo).toLocaleDateString()}`}
        </div>
      )}
      {plan.agreedBy && (
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Agreed by: {plan.agreedBy}</div>
      )}
      {plan.notes && (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text2)', fontStyle: 'italic' }}>{plan.notes}</div>
      )}
    </div>
  );
}

// ── Monthly flat callout ──────────────────────────────────────
function FlatFeeCard({ plan }) {
  if (plan?.modelType !== 'FLAT_PLUS_PER_ORDER') return null;
  const cfg        = { ...(plan.config || {}), ...(plan.customConfig || {}) };
  const monthly    = cfg.monthlyFlat ?? 2000;
  const perOrder   = cfg.perOrderFee ?? 1;

  return (
    <div className="card" style={{ background: '#1a2d3d', border: '1px solid #2a5080' }}>
      <div style={{ fontSize: 11, color: '#60b0f0', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
        Monthly Subscription
      </div>
      <div style={{ fontSize: 34, fontWeight: 800, color: '#378ADD', marginBottom: 4 }}>
        AED {fmt(monthly)}
        <span style={{ fontSize: 14, fontWeight: 400, color: '#60b0f0', marginLeft: 6 }}>/month</span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
        + AED {perOrder} per order (tracked below)
      </div>
      <div style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 8, fontSize: 12, color: '#60b0f0' }}>
        Monthly flat fee invoiced separately by your admin.
      </div>
    </div>
  );
}

// ── Stat tile ─────────────────────────────────────────────────
function Stat({ icon, label, value, prefix, color }) {
  return (
    <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '18px 14px', textAlign: 'center' }}>
      <div style={{ fontSize: 22, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || 'var(--accent)', marginBottom: 2 }}>
        {prefix && <span style={{ fontSize: 12, fontWeight: 400 }}>{prefix} </span>}
        {value}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{label}</div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function SubscriptionPage() {
  const { vendor }   = useAuth();
  const now          = new Date();
  const [year,   setYear]    = useState(now.getFullYear());
  const [month,  setMonth]   = useState(now.getMonth() + 1);
  const [sub,    setSub]     = useState(null);
  const [ledger, setLedger]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]  = useState('');

  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  const monthKey       = `${year}-${String(month).padStart(2, '0')}`;

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [subRes, ledgerRes] = await Promise.all([
        api.get('/vendors/me/subscription'),
        api.get('/vendors/me/ledger', { params: { month: monthKey, limit: 100 } }),
      ]);
      setSub(subRes.data);
      setLedger(ledgerRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [monthKey]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (isCurrentMonth) return;
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  const plan  = sub?.assignment;
  const stats = sub?.currentMonth;

  return (
    <div className="page">
      <div className="page-title">💳 Subscription & Billing</div>
      <div className="page-sub">Your platform plan, monthly fees and transaction history</div>

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, background: '#3d1a1a', color: 'var(--red)', fontSize: 13 }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>Loading billing data...</div>
      )}

      {!loading && (
        <>
          {/* Plan + flat-fee cards */}
          <div className="grid2" style={{ marginBottom: 24 }}>
            <PlanCard plan={plan} />
            {plan?.modelType === 'FLAT_PLUS_PER_ORDER'
              ? <FlatFeeCard plan={plan} />
              : (
                <div className="card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12, background: '#1a3d2a', border: '1px solid var(--green)' }}>
                  <div style={{ fontSize: 11, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: 1 }}>How it works</div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.8 }}>
                    A small platform fee is deducted from each order based on your plan.<br />
                    The fee is calculated automatically and logged below.<br />
                    Your <strong style={{ color: 'var(--text)' }}>Net Revenue</strong> = GMV − platform fee.
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                    Contact your admin to change your plan or request an invoice.
                  </div>
                </div>
              )
            }
          </div>

          {/* Month selector */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>
                {isCurrentMonth ? '📅 Current Period — ' : '📅 '}{MONTHS[month - 1]} {year}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="btn btn-ghost btn-sm" onClick={prevMonth}>← Prev</button>
                <span style={{ fontSize: 12, color: 'var(--text2)', minWidth: 90, textAlign: 'center' }}>
                  {MONTHS[month - 1]} {year}
                </span>
                <button className="btn btn-ghost btn-sm" onClick={nextMonth} disabled={isCurrentMonth}>
                  Next →
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              <Stat icon="📦" label="Orders"           value={stats?.totalOrders ?? 0}           color="var(--accent)" />
              <Stat icon="💰" label="GMV"               value={fmt(stats?.totalGMV)}    prefix="AED" color="var(--accent)" />
              <Stat icon="📊" label="Platform Fees"     value={fmt(stats?.platformFees ?? stats?.totalFees)} prefix="AED" color="var(--red)" />
              <Stat icon="✅" label="Net Revenue"       value={fmt(stats?.totalNet)}    prefix="AED" color="var(--green)" />
            </div>
            {(stats?.mallFees ?? 0) > 0 && (
              <div style={{ marginTop: 8, padding: '8px 14px', background: '#1a2d1a', borderRadius: 8, fontSize: 12, color: 'var(--green)', display: 'flex', justifyContent: 'space-between' }}>
                <span>Mall commission (paid to mall, not QuickServe)</span>
                <strong>AED {fmt(stats.mallFees)}</strong>
              </div>
            )}

            {stats?.monthlyFlat > 0 && (
              <div style={{ marginTop: 16, padding: '10px 14px', background: '#1a2d3d', borderRadius: 8, fontSize: 12, color: '#60b0f0', display: 'flex', justifyContent: 'space-between' }}>
                <span>Monthly flat fee (billed separately)</span>
                <strong>AED {fmt(stats.monthlyFlat)}</strong>
              </div>
            )}
          </div>

          {/* Transaction ledger */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700 }}>Transaction History — {MONTHS[month - 1]} {year}</span>
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>{ledger?.total ?? 0} entries</span>
            </div>

            {!ledger?.entries?.length ? (
              <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)', fontSize: 13 }}>
                No transactions for {MONTHS[month - 1]} {year}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Date', 'Order ref', 'GMV', 'Fee', 'Net', 'Breakdown'].map(h => (
                        <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.entries.map((e, i) => (
                      <tr key={e.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg3)' }}>
                        <td style={{ padding: '10px 16px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                          {new Date(e.createdAt).toLocaleDateString('en', { day: 'numeric', month: 'short' })}{' '}
                          <span style={{ color: 'var(--text3)', fontSize: 11 }}>
                            {new Date(e.createdAt).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </td>
                        <td style={{ padding: '10px 16px', fontFamily: 'monospace', color: 'var(--text2)', fontSize: 11 }}>
                          {e.orderId.slice(-8).toUpperCase()}
                        </td>
                        <td style={{ padding: '10px 16px', color: 'var(--accent)', fontWeight: 600 }}>
                          AED {fmt(e.orderValue)}
                        </td>
                        <td style={{ padding: '10px 16px', color: 'var(--red)', fontWeight: 600 }}>
                          AED {fmt(e.feeCharged)}
                          {e.recipient === 'MALL' && (
                            <span style={{ marginLeft: 4, fontSize: 10, background: '#2d3d1a', color: 'var(--green)', padding: '1px 5px', borderRadius: 4 }}>Mall</span>
                          )}
                          {(!e.recipient || e.recipient === 'PLATFORM') && (
                            <span style={{ marginLeft: 4, fontSize: 10, background: '#1a2d3d', color: '#60b0f0', padding: '1px 5px', borderRadius: 4 }}>Platform</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 16px', color: 'var(--green)', fontWeight: 600 }}>
                          AED {fmt(e.netAmount)}
                        </td>
                        <td style={{ padding: '10px 16px', color: 'var(--text3)', fontSize: 11, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {e.calculation?.breakdown || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg2)' }}>
                      <td colSpan={2} style={{ padding: '10px 16px', fontWeight: 700, fontSize: 12, color: 'var(--text2)' }}>
                        Month total ({ledger.entries.length} orders)
                      </td>
                      <td style={{ padding: '10px 16px', color: 'var(--accent)', fontWeight: 700 }}>
                        AED {fmt(ledger.entries.reduce((s, e) => s + e.orderValue, 0))}
                      </td>
                      <td style={{ padding: '10px 16px', color: 'var(--red)', fontWeight: 700 }}>
                        AED {fmt(ledger.entries.reduce((s, e) => s + e.feeCharged, 0))}
                      </td>
                      <td style={{ padding: '10px 16px', color: 'var(--green)', fontWeight: 700 }}>
                        AED {fmt(ledger.entries.reduce((s, e) => s + e.netAmount, 0))}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
